/** Visitor timezone detection + the switcher's zone list (FR-SCH-07). */

export const DEFAULT_TZ = 'America/Chicago';

export function isValidTimezone(tz: string | null | undefined): tz is string {
  if (!tz || tz === 'Etc/Unknown') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** `Intl` detection with a safe fallback for old engines / sandboxed contexts. */
export function detectTimezone(
  resolve: () => string | undefined = () => Intl.DateTimeFormat().resolvedOptions().timeZone,
): string {
  let tz: string | undefined;
  try {
    tz = resolve();
  } catch {
    tz = undefined;
  }
  return isValidTimezone(tz) ? tz : DEFAULT_TZ;
}

export const COMMON_ZONES = [
  'Pacific/Honolulu',
  'America/Anchorage',
  'America/Los_Angeles',
  'America/Vancouver',
  'America/Phoenix',
  'America/Denver',
  'America/Edmonton',
  'America/Chicago',
  'America/Mexico_City',
  'America/Winnipeg',
  'America/New_York',
  'America/Toronto',
  'America/Halifax',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'UTC',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Lisbon',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Stockholm',
  'Europe/Warsaw',
  'Europe/Athens',
  'Europe/Istanbul',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Shanghai',
  'Asia/Manila',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Perth',
  'Australia/Adelaide',
  'Australia/Brisbane',
  'Australia/Sydney',
  'Pacific/Auckland',
];

/** Short name such as "CDT" or "GMT+2" for the zone at `at`; empty when unavailable. */
export function zoneAbbreviation(tz: string, at = Date.now()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(new Date(at))
      .find((p) => p.type === 'timeZoneName');
    return parts?.value ?? '';
  } catch {
    return '';
  }
}

/** "America/Argentina/Buenos_Aires" → "Buenos Aires (GMT-3)". */
export function zoneLabel(tz: string, at = Date.now()): string {
  const city = (tz.split('/').pop() ?? tz).replace(/_/g, ' ');
  const abbr = zoneAbbreviation(tz, at);
  return abbr && abbr !== city ? `${city} (${abbr})` : city;
}

export interface ZoneOption {
  value: string;
  label: string;
}

/** The detected/selected zones always appear even when they are not in the common list. */
export function zoneOptions(...extra: string[]): ZoneOption[] {
  const seen = new Set<string>();
  const out: ZoneOption[] = [];
  for (const tz of [...extra, ...COMMON_ZONES]) {
    if (!tz || seen.has(tz) || !isValidTimezone(tz)) continue;
    seen.add(tz);
    out.push({ value: tz, label: zoneLabel(tz) });
  }
  return out;
}
